import React, { useState, useEffect, useMemo, useRef } from "react";
import Classroom from "../../../../interfaces/Classroom";
import Loading from "../../../Loading/Loading";
import ReadleeProfile from "../../../../services/firebase/ReadleeProfile";
import FirebaseService from "../../../../services/firebase/FirebaseService";
import ClassroomReadingStats from "../../../../interfaces/ClassroomReadingStats";
import ClassroomAssignmentDetails from "../../../../interfaces/ClassroomAssignmentDetails";
import Table from "../Table/Table";
import isEqual from "react-fast-compare";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import ConfirmationModal from "../../../Modals/ConfirmationModal";
import EditStudentModal from "../../../Modals/EditStudentModal";
import "./ClassPage.css";
import { ProviderContext, withSnackbar } from "notistack";
import {
  formatLastLogin,
  formatTime,
  sortDates,
  sortGrades,
  sortStrings,
  sortNumbers,
  isPreregisteredStudent,
} from "../Table/TableSortAndFormatFunctions";
import accuracyToBand, { accuracyToColor } from "../../../../utils/AccuracyToBand";
import AssignmentOnTeacher from "../../../../state/teacherData/AssignmentOnTeacher";
import { Button, Hidden, IconButton, Menu, MenuItem } from "@material-ui/core";
import useModal from "../../../Modals/UseModal";
import StandardModal from "../../../Modals/StandardModal";
import ClassCodeView from "../ClassCodeView/ClassCodeView";
import ModalSize from "../../../Modals/ModalSize";
import add from "../../../../assets/icons/noun_Plus_2190.png";
import EmptyState from "../../../EmptyState";

interface AssignmentClasses {
  // key is the assignment id
  [key: string]: {
    // key is the class id
    [key: string]: ClassroomAssignmentDetails;
  };
}

interface ClassPageProps {
  classroom: Classroom;
  assignments: AssignmentOnTeacher[];
  assignmentClasses: AssignmentClasses;
  students: Record<string, ReadleeProfile>;
}

interface StudentInfo {
  profile: ReadleeProfile;
  classroomReadingStats: ClassroomReadingStats;
  submissions: number[];
}

// useMemo with deep equality comparison function, so classPage only updates when props change
const ClassPage = React.memo((props: ClassPageProps & ProviderContext) => {
  // Loading state
  const [isLoaded, setIsLoaded] = useState(false);
  // Student info needed for table, key is student id
  const [studentInfo, setStudentInfo] = useState<Record<string, StudentInfo | { email: string }>>({});
  // Keep track of student watches
  // useRef to keep value across renders
  const studentWatches = useRef<Record<string, () => void>>({});
  const [selectedStudent, setSelectedStudent] = useState<ReadleeProfile | null>(null);
  const { open: addOpen, openModal: openAddModal, closeModal: closeAddModal } = useModal(false);
  const { open: deleteOpen, openModal: openDeleteModal, closeModal: closeDeleteModal } = useModal(false);
  const { open: editOpen, openModal: openEditModal, closeModal: closeEditModal } = useModal(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleOverflowClick = (event: React.MouseEvent<HTMLElement>, student: ReadleeProfile) => {
    event.preventDefault();
    setSelectedStudent(student);
  };

  const handleOverflowClose = () => {
    setSelectedStudent(null);
  };

  const handleCloseEditModal = () => {
    handleOverflowClose();
    closeEditModal();
  };

  const handleCloseDeleteModal = () => {
    handleOverflowClose();
    closeDeleteModal();
  };

  useEffect(() => {
    // Set us to loading state
    setIsLoaded(false);

    if (props.classroom) {
      // User and classroom exist
      setIsLoaded(true);

      // For student, watch classroomReadingStats and add to state array (StudentInfo)
      const watchStudentInfo = async (student: ReadleeProfile) => {
        // Watch classroomReadingStats for student
        studentWatches.current[student.uid] = FirebaseService.watchUserClassroomReadingStats(
          student.uid,
          props.classroom.id,
          studentClassroomReadingStats => {
            // if studentClassroomReadingStats doesn't exist, classroomReadingStats will equal undefined
            const classroomReadingStats = studentClassroomReadingStats;
            // set submissions to 0 if property doesn't exist
            const submissions =
              classroomReadingStats && classroomReadingStats.numSubmissions ? classroomReadingStats.numSubmissions : 0;
            // Get number of assignments assigned to student (length of assignment array after filtering out assignments not assigned to the student)
            const totalAssignments = props.assignments?.filter(
              (assignment: AssignmentOnTeacher) =>
                props.assignmentClasses?.[assignment.id]?.[props.classroom.id]?.students?.[student.uid] === true,
            ).length;
            // Set submissionRatio
            const submissionRatio = [submissions / totalAssignments, submissions, totalAssignments];

            setStudentInfo(state => ({
              ...state,
              [student.uid]: {
                profile: student,
                classroomReadingStats,
                submissions: submissionRatio,
              },
            }));
          },
        );
      };

      const addPreRegisteredStudents = () => {
        for (const email of props.classroom.preRegisteredStudents ?? []) {
          setStudentInfo(state => ({
            ...state,
            [email]: { email },
          }));
        }
      };

      // For each student in props.student
      for (const student in props.students) {
        //********** not sure this check is needed
        // Make sure we are not calling multiple watches on the same student
        const studentObj = props.students[student];
        if (studentObj && !studentWatches.current?.hasOwnProperty(student)) {
          watchStudentInfo(studentObj);
        }
      }

      if (props.classroom.hasOwnProperty("preRegisteredStudents")) {
        addPreRegisteredStudents();
      }
    }
    // Cleanup useEffect before rendering new students or new classroom
    const currentWatches = studentWatches.current;

    return function cleanup() {
      // cleanup watch 1for each student in studentWatches
      for (const student in currentWatches) {
        currentWatches[student]?.();
        delete currentWatches[student];
      }
    };
    // Run use effect when classroom or students change
    // props.classroom changes when assignments are added/removed from students,
    // so no need for props.assignments
  }, [props.classroom, props.students, props.assignmentClasses, props.assignments]);
  // }, [props.classroom, props.students]);

  // Columns for table
  const columns = useMemo(
    () => [
      {
        // student column
        Header: "Students",
        // if preregistered student (property email exists on student object), use email as name
        // if last name and first name don't exist on Readlee Profile, use display name
        accessor: (data: any): string =>
          data.email
            ? data.email.split("@").pop() === "readlee.com" && data.email.split("+")[0] === "clever"
              ? "Preregistered Clever Student"
              : `${data.email}`
            : data.profile.lastName && data.profile.firstName
            ? `${data.profile.lastName}, ${data.profile.firstName}`
            : `${data.profile.displayName}`,
        sortType: sortStrings,
      },
      {
        // assignment column
        Header: "Assignments",
        // accessor is what determines sorting,
        // so use the decimal value of submissions divided by assignments
        // if data doesnt exist or number of total assignments assigned to student is 0 (which makes the decimal value NaN), use undefined
        accessor: (data: any) => (data.submissions && !isNaN(data.submissions[0]) ? data.submissions[0] : undefined),
        // custom sort, so students without data stay on the bottom
        sortType: sortNumbers,
        // cell is what displays in the table, so use 'submissions/total assignments' as a string
        Cell: ({ value, row }: any) =>
          // include check for zero
          value || value === 0 ? `${row.original.submissions[1]}/${row.original.submissions[2]}` : "",
      },
      {
        // wpm column
        Header: "WPM",
        // accessor is what determines sorting, so use non-rounded value
        accessor: "classroomReadingStats.wordsPerMin",
        // custom sort, so students without data stay on the bottom
        sortType: sortNumbers,
        // cell is what displays in the table, so use WPM rounded to the nearest integer
        Cell: (props: any) =>
          // include check for zero
          props.value || props.value === 0 ? Math.round(props.value) : "",
      },
      {
        // accuracy column
        Header: "Accuracy",
        accessor: "classroomReadingStats.accuracy",
        // custom sort, so students without data stay on the bottom
        sortType: sortNumbers,
        // cell is what displays in the table, so put svg circle here
        // use accuracyToColor to get the appropriate color
        Cell: (props: any) =>
          props.value || props.value === 0 ? (
            <div className="accuracy-container">
              <svg height="16" width="16">
                <circle cx="8" cy="8" r="8" fill={accuracyToColor(props.value)} />
              </svg>
              <div className="accuracy-popup">
                {accuracyToBand(props.value)}
                <div className="caret"></div>
              </div>
            </div>
          ) : (
            ""
          ),
      },
      {
        // total words column
        Header: "Words",
        accessor: "classroomReadingStats.totalWordsRead",
        // custom sort, so students without data stay on the bottom
        sortType: sortNumbers,
      },
      {
        // reading time column
        Header: "Reading Time",
        accessor: "classroomReadingStats.totalTimeRead",
        // custom sort, so students without data stay on the bottom
        sortType: sortNumbers,
        // cell is what displays in the table, so use formatted time read
        Cell: (props: any) =>
          // if student data doesn't exist, return empty string
          // else return formatted time (include check for zero)
          props.value || props.value === 0 ? formatTime(props.value) : "",
      },
      {
        // last login column
        Header: "Last Login",
        // accessor is what determines sorting, so use date objects or return undefined
        accessor: (data: any) =>
          // if log in exists, return value as date
          data.profile?.lastLoginDay && new Date(data.profile.lastLoginDay),
        // custom sort, so students who have never logged in stay on the bottom
        sortType: sortDates,
        // cell is what displays in the table, so use formatted last login
        Cell: formatLastLogin,
      },
      {
        // grade column
        Header: "Grade",
        accessor: (data: any) => {
          return data.profile?.grade?.[0] ?? "-";
        },
        // custom sorting for grades
        sortType: sortGrades,
      },
      {
        // edit/delete student column
        // need id if no header
        id: "optionsColumn",
        // we don't want this column to have the option of sorting
        sortable: false,
        // cell is what displays in the table, so put button markdown/functionality here
        // do not display button if it is a preregistered student
        Cell: (props: any) =>
          isPreregisteredStudent(props.row) ? (
            ""
          ) : (
            <div
              ref={
                props.row.original.profile && props.row.original.profile.uid === selectedStudent?.uid ? anchorRef : null
              }
            >
              <IconButton
                size="small"
                aria-label="more"
                aria-controls="long-menu"
                aria-haspopup="true"
                onClick={e => handleOverflowClick(e, props.row.original.profile)}
                className="studentOptions"
                style={{ backgroundColor: "transparent" }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              {props.row.original.profile && props.row.original.profile.uid === selectedStudent?.uid && (
                <Menu
                  id={props.row.original.profile.uid}
                  anchorEl={anchorRef.current}
                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                  transformOrigin={{ vertical: "top", horizontal: "center" }}
                  keepMounted
                  open={Boolean(anchorRef)}
                  onClose={handleOverflowClose}
                >
                  <MenuItem
                    key={props.row.original.profile.firstName}
                    selected={editOpen}
                    onClick={e => {
                      e.preventDefault();
                      openEditModal();
                    }}
                  >
                    Edit Student
                  </MenuItem>
                  <MenuItem
                    key={props.row.original.profile.lastName}
                    selected={deleteOpen}
                    onClick={e => {
                      e.preventDefault();
                      openDeleteModal();
                    }}
                  >
                    Delete Student
                  </MenuItem>
                </Menu>
              )}
            </div>
          ),
      },
    ],
    [deleteOpen, editOpen, openDeleteModal, openEditModal, selectedStudent],
  );

  const createStudentLinkURL = (row: any) => `/Class/${props.classroom.id}/Student/${row.original.profile.uid}`;

  // If we're loading user/classroom, just return a loading spinner
  if (!isLoaded) {
    return <Loading />;
  }

  return (
    <div className="ClassPage">
      <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
        <p className="ClassTitle" title={props.classroom.className}>
          {props.classroom.className}
        </p>
        <Hidden xsDown>
          <div>
            <Button
              onClick={openAddModal}
              style={{
                textTransform: "none",
                display: "flex",
                flexDirection: "row",
                marginRight: "50px",
              }}
            >
              <img style={{ color: "#FC6954" }} height={25} src={add} alt="Add Students" />
              <p
                style={{
                  color: "#FC6954",
                  fontWeight: "bold",
                  marginBottom: "13px",
                  marginLeft: "7.5px",
                  fontSize: "15.5px",
                }}
              >
                Add Students
              </p>
            </Button>
          </div>
        </Hidden>
      </div>
      <Hidden smUp>
        <Button
          onClick={openAddModal}
          style={{
            textTransform: "none",
            display: "flex",
            flexDirection: "row",
            marginLeft: "-11px",
          }}
        >
          <img style={{ color: "#FC6954" }} height={25} src={add} alt="Add Students" />
          <p
            style={{
              color: "#FC6954",
              fontWeight: "bold",
              marginBottom: "13px",
              marginLeft: "7.5px",
              fontSize: "15.5px",
            }}
          >
            Add Students
          </p>
        </Button>
      </Hidden>
      {/* (if there are no students in classroom OR if all students in classroom === false) AND there are no preregistered students,
      show "no students," otherwise show table */}
      {(!props.classroom.students || !Object.values(props.classroom.students).includes(true)) &&
      !props.classroom.preRegisteredStudents ? (
        <EmptyState isClassPage>No Students Found</EmptyState>
      ) : (
        /* object.values is not compatible with ie */
        <Table
          kind="student"
          columns={columns}
          data={Object.entries(studentInfo)
            .filter(
              ([studentKey, _]) =>
                props.students.hasOwnProperty(studentKey) ||
                props.classroom.preRegisteredStudents?.includes(studentKey),
            )
            .map(([_, v]) => v)}
          createRowLinkURL={createStudentLinkURL}
        />
      )}
      {/* DELETE STUDENT MODAL */}
      <ConfirmationModal
        title="Remove Student?"
        onOk={async () => {
          // TODO make sure you can't spam
          // remove student from class in firebase
          selectedStudent &&
            (await FirebaseService.manageStudentOnClass(selectedStudent.uid, props.classroom.id, false));
          // use snackbar to confirm success
          props.enqueueSnackbar("Student deleted.", {
            variant: "success",
            autoHideDuration: 5000,
          });
          // close modal and reset student to empty string
          closeDeleteModal();
          setSelectedStudent(null);
        }}
        onCancel={handleCloseDeleteModal}
        open={deleteOpen}
      >
        Are you sure you want to remove this student from your class?
      </ConfirmationModal>
      {selectedStudent && (
        <EditStudentModal open={editOpen} cancelClose={handleCloseEditModal} student={selectedStudent} />
      )}
      <Hidden xsDown>
        <StandardModal
          noButtons
          title={`Add Students to ${props.classroom.className}`}
          open={addOpen}
          cancelClose={closeAddModal}
          size={ModalSize.large}
          onlyOneButton
        >
          <ClassCodeView
            classId={props.classroom.id ?? "Loading..."}
            classCode={props.classroom.classCode ?? "Loading..."}
          />
        </StandardModal>
      </Hidden>
      <Hidden smUp>
        <StandardModal
          noButtons
          title={`Add Students to ${props.classroom.className}`}
          open={addOpen}
          cancelClose={closeAddModal}
          size={ModalSize.extraSmall}
          onlyOneButton
        >
          <ClassCodeView
            classId={props.classroom.id ?? "Loading..."}
            classCode={props.classroom.classCode ?? "Loading..."}
          />
        </StandardModal>
      </Hidden>
    </div>
  );
  //    for useMemo
  //    function that checks for deep equality inorder to properly compare objects
}, isEqual);

export default withSnackbar(ClassPage);
